import React, { FC, useEffect, useState } from 'react'
import { Control, Controller, FieldValues } from 'react-hook-form'
import { Editor } from 'react-draft-wysiwyg'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'

import Button from 'components/Common/Button'
import InputField from 'components/Common/InputField'
import { EditorWrapper } from 'components/Calendar/Meetings/style'
import { isEmpty } from 'lodash'
import { CALENDAR, FORM, MEETING_LABEL, MEETING_STEPPER, USER_ROLES } from 'config'
import hookForms from 'utils/hookForms'
import {
  Divider,
  FormWrapper,
  IconWithLabel,
  InputFieldsWrapper,
  RequestMeetingWrapper,
} from 'components/Calendar/style'
import MeetingSettingsEditor, {
  MeetingSettingsEditorStyle,
} from '../../Common/MeetingSettingsEditor'
import { getRoleFromPath, meetingFormChangedValues } from 'utils/helper'
import { ampli } from 'ampli'
import Label from 'components/Common/Label'
import { ContentState, EditorState, convertFromHTML } from 'draft-js'
import uuid from 'react-uuid'
import NameInitials from 'components/Common/NameInitials'
import ProfileIcon from 'components/Common/SvgIcons/ProfileIcon'
import LinkIcon from 'components/Common/SvgIcons/LinkIcon'
import MapIcon from 'components/Common/SvgIcons/MapIcon'
import DescriptionIcon from 'components/Common/SvgIcons/DescriptionIcon'
import { UserBasicInfo, extendedCalendarEvents } from 'types'
import {
  UserProfile,
  MeetingLocation as MeetingLocationTool,
  BusinessUserType,
} from '__generated__/api-types-and-hooks'
import SelectField from 'components/Common/SelectField'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import { UseFormReturn } from 'react-hook-form'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import CrossIcon from 'components/Common/SvgIcons/CrossIcon'
import { AppSelectField } from 'components/Common/AppFormFields/AppSelectField/AppSelectField.component'
import { getTenantId } from 'utils/getTenantId'
import { useGetAdvisorDashboardQuery } from '__generated__/api-types-and-hooks'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'App'

interface IIconWithInitialsProps {
  user: UserBasicInfo
}

interface IVideoMeetingLinkProps {
  control: Control<FieldValues>
  videoLinkDescription: string
  videoLinkKey: string
  showEditButton: boolean
  isEdit: boolean
  setEditVideoMeetingLink: (param) => void
  editVideoMeetingLink: boolean
}
interface IMeetingLocationProps {
  control: Control<FieldValues>
  isEdit: boolean
  location: string
  showEditButton: boolean
}

interface IMeetingDetailsProps {
  selectedUser?: UserBasicInfo & { id: string }
  user: UserProfile
  event?: extendedCalendarEvents
  isEdit: boolean
  onSubmit: () => void
  closeModal?: () => void
  isDisabled: boolean
  description: EditorState
  setDescription: (param) => void
  showEditButton?: boolean
  setShowModalBox?: (param) => void
  setShowEditButton?: (param) => void
  updateMeetingStepperAction?: (input: { actionType: string; stepName: MEETING_STEPPER }) => void
  videoLinkDescription: string
  userAvailableMeetingLocation?: { label: string; value: string }[]
  methods: UseFormReturn
}
const MeetingDetails: FC<IMeetingDetailsProps> = ({
  selectedUser,
  user,
  event,
  isEdit,
  onSubmit,
  closeModal,
  isDisabled,
  description,
  setDescription,
  showEditButton = false,
  setShowModalBox,
  setShowEditButton,
  updateMeetingStepperAction,
  videoLinkDescription,
  userAvailableMeetingLocation,
  methods,
}) => {
  const dispatch = useDispatch()
  const [videoLinkKey, setVideoLinkKey] = useState(uuid())
  const [editVideoMeetingLink, setEditVideoMeetingLink] = useState(true)
  const [showAdditionalGuest, setShowAdditionalGuest] = useState(false)
  const [showLocation, setShowLocation] = useState(false)

  const [defaultSelectValue, setDefaultSelectValue] = useState(
    userAvailableMeetingLocation && userAvailableMeetingLocation[0]
  )
  const [defaultMeetingTool, setDefaultMeetingTool] = useState('')
  const [emailError, setEmailError] = useState('')
  const [selectedAttendees, setSelectedAttendees] = useState<IAssigneeOptions[]>([])
  const userRole = getRoleFromPath()
  const isExternalCalendar = event?.eventType ? event.eventType !== CALENDAR.INTERNAL : false
  // Fetch attendee options
  const tenantId = getTenantId()
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const isAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)
  let assigneeOptions: IAssigneeOptions[] = []

  const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })
  const businessId = isAdvisor ? event?.ownerId || selectedUser?.id || '' : user.id
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({
    enabled: true,
    paramUserId: businessId,
  })
  assigneeOptions = businessAssigneeOptions
  const { handleSubmit, control, reset, setValue } = methods
  const form = hookForms.getForm(FORM.MEETING_SCHEDULED)
  const meetingForm = form?.getValues()

  useEffect(() => {
    setShowEditButton && setShowEditButton(true)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    reset({
      ...meetingForm,
      meetingLocation: event?.location,
      videoLinkDescription: event?.videoLinkDescription ?? videoLinkDescription ?? '',
      attendees: event?.attendeesEmail?.join(', '),
    })
    setVideoLinkKey(uuid())

    // Set initial selected attendees if available
    const initialAttendees: IAssigneeOptions[] = []
    if (event?.teamMemberList?.length) {
      const selectedTeamMembers =
        event?.teamMemberDetails?.map((teamMember) => {
          return {
            label: teamMember?.firstName + ' ' + teamMember?.lastName,
            value: teamMember?.id as string,
            type: BusinessUserType.Member,
            user: {
              id: teamMember?.id as string,
              firstName: teamMember?.firstName as string,
              lastName: teamMember?.lastName as string,
            },
          }
        }) || []
      initialAttendees.push(
        ...selectedTeamMembers.map((option) => ({
          label: option.label,
          value: option.value,
          type: option.type,
          user: option.user,
        }))
      )
    }
    if (event?.attendeesEmail?.length) {
      initialAttendees.push(
        ...event?.attendeesEmail?.map((email) => ({
          label: email,
          value: email,
          type: BusinessUserType.Member,
          user: { id: '', firstName: '', lastName: '' },
        }))
      )
    }
    setSelectedAttendees(initialAttendees)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditButton, businessAssigneeOptions, advisorData])

  const handleDescriptionChange = (description) => {
    setDescription((prev) => {
      return description
    })
  }

  const participant = {
    firstName: event?.participantDetail?.firstName
      ? event?.participantDetail?.firstName
      : selectedUser?.firstName,
    lastName: event?.participantDetail?.lastName
      ? event?.participantDetail?.lastName
      : selectedUser?.lastName,
    id: event?.ownerId || selectedUser?.id,
  }

  const organizer = {
    firstName: event?.organizerDetail?.firstName
      ? event?.organizerDetail?.firstName
      : user.firstName,
    lastName: event?.organizerDetail?.lastName ? event?.organizerDetail?.lastName : user.lastName,
    id: event?.advisorId || user.id,
  }

  assigneeOptions = assigneeOptions
    .filter((option) => option.value !== loggedInUser?.id)
    .filter((option) => option.value !== participant?.id && option.value !== organizer?.id)

  useEffect(() => {
    if (defaultSelectValue) {
      setValue('meetingLocationTool', defaultSelectValue.value)
      setDefaultMeetingTool(defaultSelectValue.label)
      setDefaultSelectValue(undefined)
    }

    if (meetingForm?.meetingLocationTool === MeetingLocationTool.Custom) {
      setShowLocation(true)
    } else {
      setShowLocation(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingForm?.meetingLocationTool])

  useEffect(() => {
    if (isEdit) {
      setShowLocation(true)
    } else {
      setShowLocation(false)
    }
  }, [isEdit])

  // Update form value when selected attendees change
  useEffect(() => {
    try {
      if (selectedAttendees.length > 0) {
        const teamMemberList: string[] = []
        const attendeeEmails: string[] = []
        selectedAttendees.forEach((attendee: IAssigneeOptions) => {
          if (attendee.user.id) {
            teamMemberList.push(attendee.user.id)
          } else {
            attendeeEmails.push(attendee.value)
          }
        })
        // Use the register/setValue pattern from React Hook Form more carefully
        setValue('teamMemberList', teamMemberList, {
          shouldValidate: false, // Don't trigger validation on this change
          shouldDirty: true, // Mark field as dirty
          shouldTouch: false, // Don't mark field as touched
        })
        setValue('attendees', attendeeEmails.join(', '), {
          shouldValidate: false, // Don't trigger validation on this change
          shouldDirty: true, // Mark field as dirty
          shouldTouch: false, // Don't mark field as touched
        })
      } else {
        setValue('teamMemberList', [], {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        })
        setValue('attendees', '', {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        })
      }
    } catch (err) {
      console.error('Error updating attendees field:', err)
    }
  }, [selectedAttendees, setValue])

  const removeAttendee = (attendeeValue: string) => {
    setSelectedAttendees((prev) => prev.filter((attendee) => attendee.value !== attendeeValue))
  }

  // Add a function to check if an email is valid
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Add a function to handle adding an attendee (either from dropdown or manually entered)
  const handleAddAttendee = (selectedOption: any) => {
    if (selectedOption) {
      // The selectedOption contains the full option object with label and value
      const selectedAssignee = assigneeOptions.find(
        (option) => option.value === selectedOption.value
      )
      if (selectedAssignee && !selectedAttendees.some((a) => a.value === selectedAssignee.value)) {
        setSelectedAttendees((prev) => [...prev, selectedAssignee])
        return true
      }
    }
    return false
  }

  // Add a function to handle creating a new attendee from email
  const handleCreateAttendee = (value: string) => {
    if (isValidEmail(value) && !selectedAttendees.some((a) => a.value === value)) {
      setSelectedAttendees((prev) => [
        ...prev,
        {
          label: value,
          value: value,
          type: BusinessUserType.Member,
          user: { id: '', firstName: '', lastName: '' },
        },
      ])
      return true
    }
    return false
  }

  const isAutoGenMeetingLinksEnabled = useFeatureFlag(user.id, 'release-auto-gen-meeting-links')
  return (
    <FormWrapper
      className={`${isEdit ? 'px-6' : 'px-0'} `}
      onSubmit={(e) => {
        handleSubmit(
          () => {
            if (isEdit && event) {
              ampli.meetingUpdated({
                initiatorRole: userRole,
                initiatorName: user?.firstName + ' ' + user?.lastName,
                ...meetingFormChangedValues(meetingForm, event, description),
              })
            }
            onSubmit()
          },
          () => {
            onSubmit()
          }
        )(e)
      }}
    >
      <RequestMeetingWrapper className={'!pt-6 !pb-0'}>
        <InputFieldsWrapper>
          <div className={showEditButton ? 'cursor-not-allowed' : ''}>
            <div>
              <IconWithLabel>
                <ProfileIcon
                  className={
                    'mb-auto inline mr-3 fill-primary-disabled hover:!fill-primary-disabled'
                  }
                />
                <div className="flex flex-col ">
                  <Label fontSize={'base'} fontWeight={'font-medium'}>
                    Meeting attendees:
                  </Label>
                  {!isExternalCalendar && <IconWithInitials user={participant} />}
                  <IconWithInitials user={organizer} />
                </div>
              </IconWithLabel>
            </div>
            {/* )} */}
            <div className="pl-9">
              {!showEditButton && (
                <Button
                  label="Add additional guests"
                  variant="text"
                  className={`${showAdditionalGuest ? 'mt-6' : 'my-6'} focus:!border-0`}
                  type="button"
                  onClick={() => setShowAdditionalGuest(!showAdditionalGuest)}
                />
              )}
              <div className="mt-2 mb-2">
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedAttendees.map((attendee) => (
                    <div
                      className="bg-sky px-3 py-1 rounded-full flex items-center"
                      key={attendee.value}
                    >
                      <div className="text-black-light text-base font-semibold font-primary mr-2">
                        {attendee.label}
                      </div>
                      {!showEditButton && (
                        <div
                          className="cursor-pointer"
                          onClick={() => removeAttendee(attendee.value)}
                        >
                          <CrossIcon className="fill-black-light h-[15px] w-[15px]" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {(showEditButton ? false : showAdditionalGuest) && (
                  <div className="flex gap-4">
                    <div className="min-w-[320px] z-10">
                      <Controller
                        name="teamMemberList"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <AppSelectField
                            {...field}
                            size="md"
                            placeholder="Select team member or add attendee email"
                            isCreatable={true}
                            isSearchable={true}
                            options={assigneeOptions
                              .filter(
                                (assignee) =>
                                  !selectedAttendees.some((att) => att.value === assignee.value)
                              )
                              .map((assignee) => ({
                                label: assignee.label,
                                value: assignee.value,
                              }))}
                            onChange={(selectedOption) => {
                              // Clear error message when user selects an option
                              if (emailError) {
                                setEmailError('')
                              }
                              if (selectedOption) {
                                handleAddAttendee(selectedOption)
                              }
                            }}
                            onCreateOption={(value) => {
                              // Only create option if it's a valid email
                              if (isValidEmail(value)) {
                                if (handleCreateAttendee(value)) {
                                  setEmailError('') // Clear any previous error
                                }
                              } else {
                                // Show validation message for invalid email
                                setEmailError('Invalid email format!')
                              }
                            }}
                            formatCreateLabel={(inputValue) => {
                              return isValidEmail(inputValue)
                                ? `Add email: ${inputValue}`
                                : `${inputValue}`
                            }}
                            onInputChange={() => {
                              // Clear error message when user starts typing
                              if (emailError) {
                                setEmailError('')
                              }
                            }}
                          />
                        )}
                      />
                      {emailError && (
                        <div className="text-red-500 text-sm mt-1 ml-1">{emailError}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="pt-2">
              <Divider />
            </div>

            {/* Video meeting link */}
            {((showEditButton && (event?.videoLinkDescription || videoLinkDescription)) ||
              (!showEditButton && !isAutoGenMeetingLinksEnabled)) && (
              <div className="pt-6">
                <VideoMeetingLink
                  control={control}
                  videoLinkDescription={event?.videoLinkDescription ?? videoLinkDescription ?? ''}
                  videoLinkKey={videoLinkKey}
                  showEditButton={showEditButton}
                  isEdit={isEdit}
                  setEditVideoMeetingLink={setEditVideoMeetingLink}
                  editVideoMeetingLink={editVideoMeetingLink}
                />
              </div>
            )}

            {/* Meeting location */}
            {!isAutoGenMeetingLinksEnabled && !showEditButton && (
              <div className="flex flex-col w-fit">
                <IconWithLabel>
                  <MapIcon
                    className={`${
                      showLocation ? 'mt-6' : ''
                    } inline mr-3 fill-primary-disabled hover:!fill-primary-disabled`}
                  />
                  <Button
                    label="Add Location"
                    variant="text"
                    className={`${showLocation ? 'mt-6' : 'my-6'} focus:!border-0`}
                    type="button"
                    onClick={() => setShowLocation(!showLocation)}
                  />
                </IconWithLabel>
              </div>
            )}

            {!isAutoGenMeetingLinksEnabled && (showEditButton ? event?.location : showLocation) && (
              <IconWithLabel display={showEditButton ? 'block' : ''}>
                {showEditButton && (
                  <>
                    <MapIcon
                      className={`
                       inline mr-3 fill-primary-disabled hover:!fill-primary-disabled`}
                    />

                    <Label fontSize={'base'} fontWeight={'font-medium'}>
                      Add Location
                    </Label>
                  </>
                )}

                <div className="flex flex-col w-full pl-10">
                  <MeetingLocation
                    control={control}
                    isEdit={isEdit}
                    location={event?.location || ''}
                    showEditButton={showEditButton || false}
                  />
                </div>
              </IconWithLabel>
            )}

            {/* Meeting title */}
            <div className="pt-6">
              {!isEdit && (
                <div className={`${showLocation ? 'pt-2' : ''}`}>
                  <IconWithLabel>
                    <DescriptionIcon
                      className={
                        'mb-auto inline mr-3 fill-primary-disabled hover:!fill-primary-disabled'
                      }
                    />
                    <div className="flex flex-col w-full">
                      <Label fontSize={'base'} fontWeight={'font-medium'}>
                        Title of meeting <span className="text-primary-error">*</span>
                      </Label>
                      <Controller
                        name="meetingTitle"
                        control={control}
                        //defaultValue={isEdit ? event?.title : ''}
                        render={({ field }) => (
                          <InputField
                            defaultValue={event?.title ?? ''}
                            {...field}
                            className={
                              'xs:text-base sm:text-xl col-span-12 pb-2 !font-primary text-black-light !rounded'
                            }
                            placeholder="Enter Meeting Title"
                            readonly={showEditButton}
                          />
                        )}
                      />
                    </div>
                  </IconWithLabel>
                </div>
              )}
            </div>

            {/* Meeting location */}
            {isAutoGenMeetingLinksEnabled && !showEditButton && (
              <div className={`${showLocation ? 'pt-2' : 'mt-4'}`}>
                <IconWithLabel>
                  <MapIcon
                    className={
                      'mb-auto inline mr-3 fill-primary-disabled hover:!fill-primary-disabled'
                    }
                  />
                  <div className="flex flex-col w-full">
                    <Label fontSize={'base'} fontWeight={'font-medium'}>
                      Location
                    </Label>
                    {user.roles?.includes(USER_ROLES.BUSINESS_OWNER) ? (
                      <div className="text-sm font-bold mt-2">{defaultMeetingTool}</div>
                    ) : (
                      <Controller
                        name="meetingLocationTool"
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            {...field}
                            placeholder="Select Location"
                            options={userAvailableMeetingLocation ?? []}
                            defaultValue={defaultSelectValue}
                          />
                        )}
                      />
                    )}
                  </div>
                </IconWithLabel>
              </div>
            )}

            {isAutoGenMeetingLinksEnabled && (showEditButton ? event?.location : showLocation) && (
              <IconWithLabel display={showEditButton ? 'block' : ''}>
                {showEditButton && (
                  <>
                    <MapIcon
                      className={`
                       inline mr-3 fill-primary-disabled hover:!fill-primary-disabled`}
                    />

                    <Label fontSize={'base'} fontWeight={'font-medium'}>
                      Add Location
                    </Label>
                  </>
                )}

                <div className="flex flex-col w-full pl-10">
                  <MeetingLocation
                    control={control}
                    isEdit={isEdit}
                    location={event?.location || ''}
                    showEditButton={showEditButton || false}
                  />
                </div>
              </IconWithLabel>
            )}

            {/* Description and additional details  */}
            {(showEditButton ? event?.description : true) && (
              <div className="w-full mt-6">
                <IconWithLabel>
                  <DescriptionIcon
                    className={
                      'mb-auto inline mdl:mt-[3px] mr-3 fill-primary-disabled hover:!fill-primary-disabled'
                    }
                  />
                  <div className="flex flex-col w-full">
                    <EditorWrapper>
                      <Label fontSize={'base'} fontWeight={'font-medium'}>
                        Description and additional details
                      </Label>

                      <Editor
                        editorState={description}
                        webDriverTestID="description"
                        wrapperClassName={`${
                          showEditButton ? 'demo-wrapper-readonly' : 'demo-wrapper'
                        }`}
                        editorClassName={`${
                          showEditButton ? 'demo-editor-readonly' : 'demo-editor'
                        }`}
                        toolbarClassName={`${showEditButton ? 'demo-toolbar-readonly' : ''}`}
                        onEditorStateChange={handleDescriptionChange}
                        readOnly={showEditButton}
                      />
                    </EditorWrapper>
                  </div>
                </IconWithLabel>
              </div>
            )}
          </div>
          <div className="flex md:justify-end">
            <div className="xs:w-full md:w-fit">
              {!showEditButton && (
                <div className="flex gap-3 xs:flex-col sm:flex-row w-full items-center">
                  <Button
                    label={MEETING_LABEL.CANCEL}
                    type="button"
                    disabled={isDisabled}
                    variant={`${isEdit ? 'text' : 'secondary'}`}
                    className="xs:w-3/6 md:w-fit"
                    onClick={() => {
                      updateMeetingStepperAction &&
                        dispatch(
                          updateMeetingStepperAction({
                            actionType: 'increment',

                            stepName: MEETING_STEPPER.CLOSE_MODAL,
                          })
                        )

                      setShowAdditionalGuest(false)
                      setShowLocation(false)
                      setSelectedAttendees([])
                      if (!isEdit) {
                        closeModal && closeModal()
                        setShowModalBox && setShowModalBox(false)
                      } else {
                        setShowEditButton && setShowEditButton(true)
                        setDescription(
                          EditorState.createWithContent(
                            ContentState.createFromBlockArray(
                              convertFromHTML(event?.description || '<p></p>').contentBlocks
                            )
                          )
                        )
                        reset({
                          meetingLocation: event?.location,
                          videoLinkDescription:
                            videoLinkDescription ?? event?.videoLinkDescription ?? '',
                          attendees: event?.attendeesEmail?.join(', '),
                        })
                      }
                    }}
                  />

                  <Button
                    label={isEdit ? MEETING_LABEL.SAVE : MEETING_LABEL.CREATE_MEETING}
                    status={isDisabled ? 'loading' : ''}
                    type="submit"
                    variant="primary"
                    className="xs:w-full md:w-fit"
                    disabled={
                      (!isEdit && isEmpty(meetingForm?.meetingTitle || event?.title)) || isDisabled
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </InputFieldsWrapper>
      </RequestMeetingWrapper>
    </FormWrapper>
  )
}
MeetingDetails.defaultProps = {
  isEdit: false,
  isDisabled: false,
  showEditButton: false,
  description: undefined,
  onSubmit: () => {},
  closeModal: () => {},
  setDescription: () => {},
  // cancelEventHandler: () => {},
  updateMeetingStepperAction: () => {},
  setShowModalBox: (param) => {},
  setShowEditButton: () => {},
  selectedUser: undefined,
  event: undefined,
}
export default MeetingDetails

export const IconWithInitials: FC<IIconWithInitialsProps> = ({ user }) => {
  return (
    <div className="flex gap-3 items-center mt-3">
      <div>
        <NameInitials
          name={{
            firstName: user.firstName || '',
            lastName: user.lastName || '',
          }}
        />
      </div>
      <span className="text-lg font-bold">
        {user.firstName} {user.lastName}
      </span>
    </div>
  )
}

export const VideoMeetingLink: FC<IVideoMeetingLinkProps> = ({
  control,
  videoLinkDescription,
  videoLinkKey,
  showEditButton,
  isEdit,
  setEditVideoMeetingLink,
  editVideoMeetingLink,
}) => {
  return (
    <div className="text-base sm:text-lg my-2 flex items-center">
      <LinkIcon
        className={'mb-auto inline mr-3 fill-primary-disabled hover:!fill-primary-disabled'}
      />
      <div className="w-full">
        <div className="flex justify-between">
          <Label fontSize={'base'} fontWeight={'font-medium'}>
            Video meeting link
          </Label>
          {!isEdit && videoLinkDescription && (
            <div>
              <Button
                label="Edit"
                variant="text"
                type="button"
                className="w-[50px]"
                onClick={() => {
                  setEditVideoMeetingLink(!editVideoMeetingLink)
                }}
              />
            </div>
          )}
        </div>

        <Controller
          name={'videoLinkDescription'}
          control={control}
          defaultValue={videoLinkDescription}
          render={({ field }) => (
            <MeetingSettingsEditor
              {...field}
              key={videoLinkKey}
              style={MeetingSettingsEditorStyle.SECONDARY}
              disabled={!isEdit && videoLinkDescription ? editVideoMeetingLink : showEditButton}
            />
          )}
        />
      </div>
    </div>
  )
}

export const MeetingLocation: FC<IMeetingLocationProps> = ({
  control,
  isEdit,
  location,
  showEditButton,
}) => {
  return (
    <div>
      <Controller
        name="meetingLocation"
        control={control}
        defaultValue={isEdit ? location : ''}
        render={({ field }) => (
          <InputField
            {...field}
            placeholder="Enter Location"
            // label={MEETING_LABEL.LOCATION}
            className={`col-span-12 !font-medium !text-base !font-primary text-black-light ${
              showEditButton ? 'bg-white border-none !px-0 !mt-2' : '!rounded'
            }`}
            readonly={showEditButton}
            labelFontWeight={`${showEditButton ? 'font-bold' : 'font-medium'}`}
            labelFontSize="base"
          />
        )}
      />
    </div>
  )
}
